/**
 * FailureRecoveryPanel — the four recovery questions for one agent run, in operator Hebrew:
 * what failed, what was preserved, what input/approval is needed, and whether a retry/resume
 * is available. Purely a read model over fields the run APIs already return; the caller owns
 * any action buttons (the run detail page already has its own retry/resume controls).
 */
import { buildRecoveryBrief } from "../utils/recoveryInsights";

const TONE_CLASS = {
  ok:      "bg-green-50 text-green-800 border-green-200",
  warn:    "bg-amber-50 text-amber-800 border-amber-200",
  neutral: "bg-gray-50 text-gray-700 border-gray-200",
};

export default function FailureRecoveryPanel({
  run,
  canRun = false,
  actions = null,
  title = "מה קרה ומה הלאה",
  className = "bg-white rounded-xl border shadow-sm p-5 space-y-4",
}) {
  if (!run) return null;
  const brief = buildRecoveryBrief(run, { canRun });
  const { failure, preserved, needed, action } = brief;

  return (
    <section dir="rtl" data-testid="failure-recovery-panel" className={className}>
      {title && <h2 className="font-semibold text-gray-700 text-sm border-b pb-2">{title}</h2>}

      <div className="space-y-3">
        {/* What failed */}
        <div data-testid="frp-failed" className="rounded-lg border border-red-100 bg-red-50/60 px-3 py-2.5">
          <p className="text-xs font-semibold text-red-700 mb-0.5">מה נכשל</p>
          <p data-testid="frp-failure-title" className="text-sm text-gray-800">{failure.title}</p>
          {failure.detail && (
            <p data-testid="frp-failure-detail" className="mt-1 text-xs text-gray-500">{failure.detail}</p>
          )}
        </div>

        {/* What was preserved */}
        <div data-testid="frp-preserved" className="rounded-lg border border-gray-200 px-3 py-2.5">
          <p className="text-xs font-semibold text-gray-600 mb-1.5">מה נשמר</p>
          {preserved.length > 0 ? (
            <ul className="space-y-1">
              {preserved.map((item) => (
                <li key={item.key}>
                  <span className={`inline-block text-xs border rounded px-2 py-0.5 ${TONE_CLASS[item.tone] ?? TONE_CLASS.neutral}`}>
                    {item.label}
                  </span>
                </li>
              ))}
            </ul>
          ) : (
            <p data-testid="frp-preserved-unknown" className="text-xs text-gray-400">
              לא ידוע — לא נמצא מידע שמור לריצה הזו
            </p>
          )}
        </div>

        {/* What input / approval is needed */}
        <div data-testid="frp-needed" className="rounded-lg border border-blue-100 bg-blue-50/50 px-3 py-2.5">
          <p className="text-xs font-semibold text-blue-700 mb-0.5">מה נדרש כדי להתקדם</p>
          <p className="text-sm text-gray-800">{needed.title}</p>
          {needed.detail && <p className="mt-1 text-xs text-gray-500">{needed.detail}</p>}
        </div>

        {/* Retry / resume availability */}
        <div
          data-testid="frp-action"
          className={`rounded-lg border px-3 py-2.5 ${action.available ? "border-green-200 bg-green-50" : "border-gray-200 bg-gray-50"}`}
        >
          <p className="text-xs font-semibold text-gray-600 mb-0.5">ניסיון חוזר</p>
          {action.available ? (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm text-green-800">ניתן לנסות שוב מהמסך הזה</span>
              {actions}
            </div>
          ) : (
            <p data-testid="frp-action-reason" className="text-sm text-gray-600">{action.reason}</p>
          )}
        </div>
      </div>
    </section>
  );
}
